import { ComponentType, FC, PropsWithChildren, useContext, useRef } from "react";
import { FormDefinitions, StagePricelist, StagePricelistGetByStageIdSubStageResponse, SubStages } from "../../api/dtos";
import { FormContext, FormDefinitionContext, FormDefinitionProvider, LookupsContext, useFormById, useFormMetadata, useLookups } from "@ngt/forms";
import useStagePricelistByStageIdSubStage from "../../hooks/data/useStagePricelistByStageIdSubStage";

export interface IStagePricelistBeforeMount {
    formDefinition?: boolean;
    form?: boolean;
    lookups?: boolean;
}

export interface IStagePricelistProviderProps extends PropsWithChildren {
        stageId?: number | null;
        subStage?: SubStages;
    
        formDefinitionIdentifier?: string | number;
    
        loadBeforeMount?: boolean | IStagePricelistBeforeMount;
        LoadingComponent?: ComponentType<any>;
    
        validateBeforeMount?: boolean | IStagePricelistBeforeMount;
        ValidatingComponent?: ComponentType<any>;
};


export const InternalStagePricelistProvider: FC<IStagePricelistProviderProps> = ({ stageId, subStage: subStage, children, loadBeforeMount, LoadingComponent, validateBeforeMount, ValidatingComponent }) => {

    const formDefinitionContext = useContext(FormDefinitionContext);

    const formMetadata = useFormMetadata(formDefinitionContext.data?.id);

    const disableFormFlashWorkaround = useRef<StagePricelist>(undefined)

    const formContext = useStagePricelistByStageIdSubStage(
        stageId,
        subStage,
        {
            fallbackData: new StagePricelistGetByStageIdSubStageResponse({data: disableFormFlashWorkaround.current})
        }
    ) as unknown as ReturnType<typeof useFormById>;

    if (formContext.data) {
        disableFormFlashWorkaround.current = formContext.data as any;
    }

    const lookupsContext = useLookups(formMetadata);

    const loading = (
        (loadBeforeMount && (loadBeforeMount === true || loadBeforeMount?.formDefinition === true) && formDefinitionContext.loading) ||
        (loadBeforeMount && (loadBeforeMount === true || loadBeforeMount?.form === true) && formContext.loading) ||
        (loadBeforeMount && (loadBeforeMount === true || loadBeforeMount?.lookups === true) && lookupsContext.loading)
    );

    const validating = (
        (validateBeforeMount && (validateBeforeMount === true || validateBeforeMount?.formDefinition === true) && formDefinitionContext.validating) ||
        (validateBeforeMount && (validateBeforeMount === true || validateBeforeMount?.form === true) && formContext.validating) ||
        (validateBeforeMount && (validateBeforeMount === true || validateBeforeMount?.lookups === true) && lookupsContext.validating)
    );

    return (
        <FormContext.Provider value={formContext}>
            <LookupsContext.Provider value={lookupsContext}>
                {
                    loading ? 
                        LoadingComponent ?
                            <LoadingComponent /> :
                            "Loading" :
                        validating ? 
                            ValidatingComponent ?
                                <ValidatingComponent /> :
                                "Validating" :
                            children
                }
            </LookupsContext.Provider>
        </FormContext.Provider>
    );
}

export const StagePricelistProvider: FC<IStagePricelistProviderProps> = ({ children, ...props }) => {
    return (
        <FormDefinitionProvider
            formDefinitionIdentifier={FormDefinitions.StagePricelist}
        >
            <InternalStagePricelistProvider
                {...props}
            >
                {children}
            </InternalStagePricelistProvider>
        </FormDefinitionProvider>
    );
}

export default StagePricelistProvider;